import { Link } from 'react-router-dom';
import { ArrowRight, Heart } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { BreadcrumbSchema } from '@/components/seo/BreadcrumbSchema';
import { CATEGORIES, CATEGORY_ORDER } from '@/data/livingAnnouncementExamples';

export default function LivingAnnouncementsIndex() {
  const ogImageUrl = 'https://say-it-now.app/og/living-announcements.jpg';

  useDocumentHead({
    title: 'Living Announcements — Real Words for the People You Love | Say It NOW',
    description:
      'Browse example Living Announcements by emotion — love, gratitude, family, birthday, anniversary, apology. Real words, sent while they’re still here to read them.',
    path: '/living-announcements',
    meta: [
      { property: 'og:type', content: 'website' },
      { name: 'robots', content: 'index, follow, max-image-preview:large' },
      { property: 'og:image', content: ogImageUrl },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:type', content: 'image/jpeg' },
      { property: 'og:image:alt', content: 'Living Announcements — Celebrate people while they’re alive.' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: ogImageUrl },
    ],
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Living Announcements Gallery',
    url: 'https://say-it-now.app/living-announcements',
    description:
      'A curated gallery of example Living Announcements across love, gratitude, family, birthday, anniversary and apology.',
    hasPart: CATEGORY_ORDER.map((slug) => ({
      '@type': 'CreativeWork',
      name: CATEGORIES[slug].h1,
      url: `https://say-it-now.app/living-announcements/${slug}`,
    })),
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <BreadcrumbSchema
        items={[
          { name: 'Home', path: '/' },
          { name: 'Living Announcements', path: '/living-announcements' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Header />

      <main className="flex-1">
        <section className="relative overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 80% 60% at 50% 0%, hsl(var(--primary) / 0.18), transparent 70%)',
            }}
          />
          <div className="relative container max-w-4xl px-5 pt-20 pb-14 md:pt-32 md:pb-20 text-center">
            <p className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-primary/80 mb-6">
              Living Announcements · Gallery
            </p>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-8">
              Words for the people who are still here.
            </h1>
            <p className="text-lg md:text-2xl leading-relaxed text-muted-foreground max-w-2xl mx-auto">
              Real example Living Announcements — written for real moments. Choose the emotion that fits the person you’ve been meaning to write to.
            </p>
          </div>
        </section>

        <div className="container max-w-4xl px-5">
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        </div>

        <section className="container max-w-5xl px-5 py-14 md:py-20">
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CATEGORY_ORDER.map((slug, i) => {
              const c = CATEGORIES[slug];
              return (
                <li
                  key={slug}
                  className="motion-safe:animate-fade-in"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <Link
                    to={`/living-announcements/${slug}`}
                    className="group block h-full rounded-2xl border border-border bg-card/60 backdrop-blur-sm p-7 md:p-8
                      transition-all duration-500 hover:border-primary/40 hover:-translate-y-1"
                  >
                    <p className="text-[10px] uppercase tracking-[0.28em] text-primary/80 mb-3">
                      {c.eyebrow.split('·').pop()?.trim()}
                    </p>
                    <h2 className="font-heading text-2xl md:text-[28px] leading-tight tracking-tight text-foreground mb-4">
                      {c.h1}
                    </h2>
                    <p className="text-[15px] leading-relaxed text-muted-foreground mb-6">
                      {c.lede}
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm text-primary">
                      See examples
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="container max-w-3xl px-5 pb-20">
          <div className="border-t border-border pt-12 md:pt-16 text-center">
            <p className="font-heading text-2xl md:text-4xl leading-tight mb-3 tracking-tight">
              Celebrate people while they’re alive.
            </p>
            <p className="text-muted-foreground mb-8">
              Don’t wait for a eulogy to say the thing.
            </p>
            <Button asChild size="lg" className="rounded-full px-7 h-12 gap-2">
              <Link to="/signup">
                <Heart className="w-4 h-4" aria-hidden /> Start your announcement
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
